import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Star,
  Award,
  CheckCircle2,
  Code,
  Cpu,
  Bot,
  Quote,
  MessageSquare,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  mockCurriculum,
  mockWorkshops,
  mockCamps,
  mockFeaturedRobotProjects,
  homeHowItWorksSteps,
  homeFeatures,
  homeAgeLevels,
  homeCoreValues,
  buildHomeStats,
} from '../data/mock';
import { useSettings, useBrandName } from '../context/SettingsContext';
import EditableText from '../components/EditableText';
import { BACKEND_URL, BTN_PRIMARY, BTN_OUTLINE, RADIUS_SURFACE, RADIUS_PILL, mediaSrc } from '../config/brand';
import { IMAGES, WORKSHOP_GALLERY } from '../config/images';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PartnersCarousel from '../components/PartnersCarousel';
import RobotGallery from '../components/RobotGallery';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import HowItWorks from '../components/HowItWorks';
import FeatureGrid from '../components/FeatureGrid';
import StatsBand from '../components/StatsBand';
import AgeLevels from '../components/AgeLevels';
import SectionHeader from '../components/SectionHeader';
import IconBadge from '../components/IconBadge';
import RobotMascot from '../components/RobotMascot';
import { resolveHomeIcon } from '../lib/homeIcons';
import { useTestimonials } from '../hooks/useTestimonials';

const DEFAULT_OFFER_CARDS = [
  {
    id: 'atelier',
    title: 'Ateliers à la Carte',
    description: 'Formules flexibles adaptées à vos besoins',
    image: mockWorkshops[0].image,
    benefits: [
      'Atelier découverte OFFERT',
      'Défi Mercredi - 25€',
      'Cours particulier - 60€',
      'Formules sur mesure',
    ],
    link: '/reserver?type=atelier',
    cta: 'Découvrir les ateliers',
    popular: false,
  },
  {
    id: 'cursus',
    title: 'Cursus Annuel',
    description: 'Formation complète sur une année scolaire',
    image: mockCurriculum[0].image,
    benefits: [
      '30 séances progressives',
      'Matériel fourni sur place',
      'Accès e-learning gratuit',
      '1150€ / an',
    ],
    link: '/reserver?type=cursus',
    cta: 'Réserver',
    popular: true,
  },
  {
    id: 'stage',
    title: 'Stages Vacances par Thème',
    description: 'Choisissez votre thème et votre durée (10h ou 20h)',
    image: mockCamps[0].image,
    benefits: [
      '6 thèmes disponibles',
      '10h (240€) ou 20h (320€)',
      'Tous âges, tous niveaux',
    ],
    link: '/reserver?type=stage',
    cta: 'Réserver',
    popular: false,
  },
];

const formatFormulaBenefit = (formula) => {
  const priceLabel = formula.price === 0 ? 'OFFERT' : `${formula.price}€`;
  return `${formula.name} - ${priceLabel}`;
};

const mapOffersToCards = (offers) => {
  const byCategory = { atelier: [], cursus: [], stage: [] };
  for (const offer of offers) {
    if (byCategory[offer.category]) byCategory[offer.category].push(offer);
  }

  const cards = [];

  if (byCategory.atelier.length > 0) {
    const primary = byCategory.atelier[0];
    const benefits = byCategory.atelier
      .flatMap((o) => (o.formulas?.length ? o.formulas.map(formatFormulaBenefit) : o.benefits || []))
      .slice(0, 4);
    cards.push({
      id: 'atelier',
      title: 'Ateliers à la Carte',
      description: primary.short_description || 'Formules flexibles adaptées à vos besoins',
      image: primary.image_url || mockWorkshops[0].image,
      benefits: benefits.length ? benefits : DEFAULT_OFFER_CARDS[0].benefits,
      link: '/reserver?type=atelier',
      cta: 'Découvrir les ateliers',
      popular: false,
    });
  }

  const cursus = byCategory.cursus[0];
  if (cursus) {
    const minPrice = cursus.formulas?.length
      ? Math.min(...cursus.formulas.map((f) => f.price))
      : null;
    cards.push({
      id: 'cursus',
      title: cursus.title,
      description: cursus.short_description || cursus.description,
      image: cursus.image_url || DEFAULT_OFFER_CARDS[1].image,
      benefits: (cursus.benefits?.length ? cursus.benefits : DEFAULT_OFFER_CARDS[1].benefits).slice(0, 4),
      link: `/reserver?type=cursus&offer=${cursus.slug}`,
      cta: 'Réserver',
      popular: true,
      priceHint: minPrice != null ? `${minPrice}€ / an` : null,
    });
  }

  const stage = byCategory.stage[0];
  if (stage) {
    cards.push({
      id: 'stage',
      title: stage.title,
      description: stage.short_description || stage.description,
      image: stage.image_url || mockCamps[0].image,
      benefits: (stage.benefits?.length ? stage.benefits : DEFAULT_OFFER_CARDS[2].benefits).slice(0, 4),
      link: `/reserver?type=stage&offer=${stage.slug || 'stage-vacances'}`,
      cta: 'Réserver',
      popular: false,
    });
  }

  return cards.length > 0 ? cards : DEFAULT_OFFER_CARDS;
};

const renderHeroTitle = (title) => {
  const accentMatch = title.match(/^(.*?)(dès\s+\d+\s+ans)\.?$/i);
  if (accentMatch) {
    return (
      <>
        {accentMatch[1].trim()}{' '}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3a00e0] via-[#fab03a] to-[#c47a0a]">
          {accentMatch[2]}
        </span>
      </>
    );
  }
  return title;
};

const highlightWord = (text, word, className = 'text-gradient-brand') => {
  const idx = text.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className={className}>{text.slice(idx, idx + word.length)}</span>
      {text.slice(idx + word.length)}
    </>
  );
};

const Home = () => {
  const { settings } = useSettings();
  const brandName = useBrandName();
  const { isAdmin, loading: authLoading } = useAuth();
  const testimonials = useTestimonials();
  const [offerCards, setOfferCards] = useState(DEFAULT_OFFER_CARDS);

  const centersCount = settings.centers_count ?? 5;
  const homeStats = useMemo(() => buildHomeStats(centersCount), [centersCount]);

  useEffect(() => {
    if (!BACKEND_URL) return;
    const loadOffers = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/offers`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setOfferCards(mapOffersToCards(data));
        }
      } catch {
        // fallback static cards
      }
    };
    loadOffers();
  }, []);

  const heroUrl = settings.hero_image_url || '';
  /** Ignore Unsplash, vieux /hero.jpg, et scènes « mascotte seule » (cutout / 3D) */
  const isStaleHeroBg =
    !heroUrl ||
    heroUrl.includes('unsplash.com') ||
    heroUrl.endsWith('/hero.jpg') ||
    heroUrl.includes('mascotte-scene') ||
    heroUrl.includes('robot-mascot') ||
    heroUrl.includes('robot-seul');
  /** Fond full-bleed : photo atelier humains (défaut) ou override admin photo */
  const heroVisual = isStaleHeroBg ? IMAGES.heroAtelier : heroUrl;
  const heroTitleDefault = settings.hero_title || 'Robotique, Programmation & IA dès 6 ans';
  const heroSubtitleDefault =
    settings.hero_subtitle ||
    `Ateliers, stages et formations STEM pour préparer vos enfants au monde de demain avec ${brandName}.`;
  /** Defaults front — si la DB a encore « CTA test », clear via admin ou `npm run seed -- --reset-hero-cta` */
  const heroCtaDefault = 'Réserver un atelier';
  const heroCtaSecondaryDefault = `Découvrir ${brandName}`;

  return (
    <div className="min-h-screen bg-white">
      {!authLoading && isAdmin && (
        <Link
          to="/admin"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/95 backdrop-blur-sm border border-[rgba(58,0,224,0.25)] text-[#3a00e0] shadow-lg shadow-[0_10px_30px_-12px_rgba(58,0,224,0.35)] hover:border-[rgba(250,176,58,0.55)] hover:text-[#2f00b8] transition-all group beep-hover"
          aria-label="Administration"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[rgba(58,0,224,0.12)] to-[rgba(250,176,58,0.2)] border border-[rgba(58,0,224,0.18)]">
            <Bot className="h-4 w-4 text-[#c47a0a] admin-bot-pulse group-hover:scale-110 transition-transform duration-300" />
          </span>
          <span className="text-sm font-medium pr-1">Administration</span>
        </Link>
      )}
      <Navbar />

      {/* Hero full-bleed — photo atelier + mascotte cutout accent ; stats hors hero */}
      <section
        className="relative flex flex-col overflow-hidden min-h-[min(92svh,880px)] lg:min-h-[min(100svh,920px)]"
        aria-label={`Accueil ${brandName}`}
      >
        <div className="absolute inset-0" aria-hidden="true">
          <div className="hero-fullbleed-media-wrap absolute inset-0">
            <img
              src={mediaSrc(heroVisual)}
              alt=""
              className="hero-fullbleed-media absolute inset-0 h-full w-full object-cover object-[72%_center] sm:object-[65%_center] lg:object-[58%_42%]"
            />
          </div>
          <div className="hero-fullbleed-scrim absolute inset-0" />
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-[58%] overflow-hidden opacity-[0.2]"
            aria-hidden="true"
          >
            <div
              className="hero-dots-legacy"
              style={{
                WebkitMaskImage: `url(${IMAGES.legacy.dots})`,
                maskImage: `url(${IMAGES.legacy.dots})`,
              }}
            />
            <div
              className="hero-dots-legacy hero-dots-legacy--accent"
              style={{
                WebkitMaskImage: `url(${IMAGES.legacy.dots})`,
                maskImage: `url(${IMAGES.legacy.dots})`,
              }}
            />
          </div>
          {/* Mascotte cutout — accent flottant à droite, pas l’image principale */}
          <div className="pointer-events-none absolute inset-y-0 right-0 z-[1] flex w-[42%] max-w-md items-end justify-center pb-4 sm:w-[38%] sm:max-w-lg sm:pb-6 lg:w-[34%] lg:max-w-xl lg:pb-8 xl:right-[2%]">
            <img
              src={IMAGES.robotMascot}
              alt=""
              className="hero-cutout-float h-[min(42vh,340px)] w-auto max-w-[90%] object-contain object-bottom drop-shadow-[0_20px_36px_rgba(24,68,158,0.32)] sm:h-[min(48vh,400px)] lg:h-[min(52vh,440px)]"
            />
          </div>
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 sm:px-6 pt-below-site-header pb-14 sm:pb-16 lg:pb-20">
          <Reveal animation="fade-up" className="max-w-xl lg:max-w-[34rem] xl:max-w-[38rem]">
            <p className="hero-brand-mark mb-5 sm:mb-6">{brandName}</p>

            <EditableText
              editKey="home.hero.title"
              defaultValue={heroTitleDefault}
              as="h1"
              className="mb-5 text-[2.15rem] font-bold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.35rem] xl:text-[3.65rem]"
              formatDisplay={renderHeroTitle}
            />

            <EditableText
              editKey="home.hero.subtitle"
              defaultValue={heroSubtitleDefault}
              as="p"
              multiline
              className="mb-8 max-w-lg text-base leading-relaxed text-slate-700 sm:mb-9 sm:text-lg"
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
              <Link to="/reserver">
                <Button size="lg" className={`w-full sm:w-auto ${BTN_PRIMARY} px-8 py-6 text-lg btn-beep`}>
                  <EditableText editKey="home.hero.cta" defaultValue={heroCtaDefault} className="inline" />
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  size="lg"
                  variant="outline"
                  className={`w-full sm:w-auto ${BTN_OUTLINE} border-white/80 bg-white/90 px-8 py-6 text-lg backdrop-blur-sm btn-beep`}
                >
                  <EditableText editKey="home.hero.ctaSecondary" defaultValue={heroCtaSecondaryDefault} className="inline" />
                </Button>
              </Link>
            </div>
          </Reveal>

          <span className="sr-only">
            Scène atelier {brandName} : enfants en atelier et mascotte robotique
          </span>
        </div>
      </section>

      <StatsBand stats={homeStats} />

      <HowItWorks steps={homeHowItWorksSteps} />

      {/* Why STEM */}
      <section className="section-y px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Notre approche"
            eyebrowKey="home.why.eyebrow"
            title="Pourquoi la robotique et la programmation ?"
            titleKey="home.section.why.title"
            subtitle={`${brandName} propose des programmes STEM structurés qui développent la curiosité, la logique et la confiance en soi — des compétences essentielles pour l'école et les métiers de demain.`}
            subtitleKey="home.why.subtitle"
            formatTitle={(t) => highlightWord(t, 'robotique')}
            reveal
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {[
              { icon: Code, title: 'S.T.E.M Education', desc: 'Programmes éducatifs captivants pour les enfants de 6 à 18 ans. La programmation ouvre de nouvelles opportunités et favorise une réflexion innovante.', n: 1 },
              { icon: Cpu, title: 'Pédagogie Adaptée', desc: "Plus de 50 modules conçus par des experts. Apprentissage ludique et sécurisé avec progression personnalisée par groupes d'âge.", n: 2 },
              { icon: Award, title: 'Opportunités', desc: "Développement des compétences STEM à travers des concours et projets. Renforce la confiance et l'esprit critique des enfants.", n: 3 },
            ].map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 55}>
                <div className="h-full">
                  <IconBadge icon={item.icon} variant={i === 1 ? 'orange' : 'purple'} size="md" className="mb-4" />
                  <EditableText
                    editKey={`home.why.${item.n}.title`}
                    defaultValue={item.title}
                    as="h3"
                    className="text-slate-900 text-lg font-semibold mb-2"
                  />
                  <EditableText
                    editKey={`home.why.${item.n}.text`}
                    defaultValue={item.desc}
                    as="p"
                    multiline
                    className="text-slate-600 text-sm leading-relaxed"
                  />
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-6 md:grid-cols-4 md:gap-x-8">
            {homeCoreValues.map((value, i) => {
              const ValueIcon = resolveHomeIcon(value.icon);
              return (
                <Reveal key={value.title} animation="fade-up" delay={i * 40}>
                  <div className="text-center">
                    <IconBadge
                      icon={ValueIcon}
                      variant={value.variant}
                      size="sm"
                      className="mx-auto mb-2.5"
                    />
                    <EditableText
                      editKey={`home.values.${i + 1}.title`}
                      defaultValue={value.title}
                      as="h3"
                      className="text-slate-900 font-medium text-sm"
                    />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <div className="py-2">
        <CircuitDivider />
      </div>

      <AgeLevels levels={homeAgeLevels} />

      <FeatureGrid features={homeFeatures} brandName={brandName} />

      {/* Workshop photo band */}
      <section className="section-y px-4">
        <div className="max-w-7xl mx-auto">
          <Reveal animation="fade-up">
            <div className={`relative overflow-hidden ${RADIUS_SURFACE}`}>
              <img
                src={WORKSHOP_GALLERY[0]}
                alt={`Atelier robotique en cours chez ${brandName}`}
                className="h-56 w-full object-cover sm:h-72 lg:h-80"
              />
              <div className="absolute inset-0 flex items-center bg-gradient-to-r from-[#18449e]/88 via-[#22266a]/45 to-transparent">
                <div className="max-w-lg p-8 sm:p-12">
                  <EditableText
                    editKey="home.workshop.eyebrow"
                    defaultValue="En atelier"
                    as="p"
                    className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#fab03a] sm:text-sm"
                  />
                  <EditableText
                    editKey="home.workshop.title"
                    defaultValue="Apprendre en construisant de vrais robots"
                    as="h3"
                    className="mb-3 text-2xl font-bold tracking-tight text-white sm:text-3xl"
                  />
                  <EditableText
                    editKey="home.workshop.subtitle"
                    defaultValue="Chaque séance mêle montage, programmation et créativité — dans nos centres équipés sur la Côte d'Azur."
                    as="p"
                    multiline
                    className="mb-6 text-sm leading-relaxed text-white/90 sm:text-base"
                  />
                  <Link to="/ateliers">
                    <Button className={BTN_PRIMARY}>
                      <EditableText editKey="home.workshop.cta" defaultValue="Voir nos ateliers" className="inline" />
                      <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Main Offers — cards = interaction (CTA réservation) */}
      <section className="section-y px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos formules"
            eyebrowKey="home.offers.eyebrow"
            title="Nos Offres Principales"
            titleKey="home.section.offers.title"
            subtitle="Trois parcours adaptés à chaque rythme — de la découverte aux formations annuelles complètes"
            subtitleKey="home.section.offers.subtitle"
            formatTitle={(t) => highlightWord(t, 'Offres')}
            reveal
          />

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {offerCards.map((card, i) => (
              <Reveal key={card.id} animation="fade-up" delay={i * 70}>
                <Card
                  className={
                    card.popular
                      ? 'relative h-full overflow-hidden border-[#3a00e0] bg-white shadow-lg shadow-[0_14px_36px_-18px_rgba(58,0,224,0.25)] ring-1 ring-[rgba(58,0,224,0.1)] md:col-span-2 lg:col-span-1 group'
                      : 'h-full overflow-hidden border-slate-200 bg-white shadow-sm transition-colors hover:border-[rgba(58,0,224,0.22)] group'
                  }
                >
                  {card.popular && (
                    <div className="absolute top-3 right-3 z-10 bg-[#3a00e0] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      <EditableText editKey="home.offers.popular_badge" defaultValue="Populaire" as="span" />
                    </div>
                  )}
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl tracking-tight text-slate-900 sm:text-2xl">
                      {card.title}
                    </CardTitle>
                    <CardDescription className="text-slate-600 leading-relaxed">
                      {card.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {card.benefits.map((benefit, j) => (
                        <div
                          key={`${card.id}-benefit-${j}`}
                          className="flex items-start text-sm text-slate-600"
                        >
                          <CheckCircle2 className="mr-2 mt-0.5 h-4 w-4 shrink-0 text-[#3a00e0]" aria-hidden="true" />
                          <span>{benefit}</span>
                        </div>
                      ))}
                      {card.priceHint && (
                        <div className="flex items-start text-sm text-slate-600">
                          <CheckCircle2 className="mr-2 mt-0.5 h-4 w-4 shrink-0 text-[#3a00e0]" aria-hidden="true" />
                          <span>{card.priceHint}</span>
                        </div>
                      )}
                    </div>
                    <Link to={card.link} className="block">
                      <Button className={`w-full ${BTN_PRIMARY}`}>
                        <EditableText
                          editKey={`home.offers.${card.id}.cta`}
                          defaultValue={card.cta}
                          as="span"
                        />
                        <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <RobotGallery
        projects={mockFeaturedRobotProjects}
        limit={8}
        showExpand
        expandStep={4}
        itemNameKeyPrefix="home.gallery"
        eyebrowKey="home.gallery.eyebrow"
        title="Nos projets robotiques"
        titleKey="home.gallery.title"
        subtitle="Des dizaines de projets Makeblock et Artec réalisés en atelier — voiture, sumo, lanceur, bipède et plus encore."
        subtitleKey="home.gallery.subtitle"
        cta={{ to: '/ateliers', label: 'Voir tous les projets' }}
        ctaKey="home.gallery.cta"
      />

      {/* Testimonials */}
      <section className="section-y px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Témoignages"
            eyebrowKey="home.testimonials.eyebrow"
            title="Ce que disent les parents"
            titleKey="home.testimonials.title"
            subtitle="Plus de 200 familles nous font confiance"
            subtitleKey="home.testimonials.subtitle"
            formatTitle={(t) => highlightWord(t, 'parents')}
            reveal
          />

          {!authLoading && isAdmin && (
            <div className="mb-6 flex justify-center sm:justify-end">
              <Link
                to="/admin/testimonials"
                className={`inline-flex items-center gap-2 ${RADIUS_SURFACE} border border-[rgba(58,0,224,0.18)] bg-white/80 px-3 py-2 text-sm text-[#3a00e0] transition-colors hover:bg-white`}
              >
                <MessageSquare className="h-4 w-4 shrink-0" aria-hidden="true" />
                Gérer les avis
              </Link>
            </div>
          )}

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((testimonial, i) => (
              <Reveal key={testimonial.id} animation="fade-up" delay={i * 50}>
                <blockquote className="relative h-full">
                  <Quote className="absolute -top-1 right-0 h-6 w-6 text-[#3a00e0]/12" aria-hidden="true" />
                  <div className="mb-3 flex items-center gap-3">
                    {testimonial.avatar ? (
                      <img
                        src={testimonial.avatar}
                        alt=""
                        className="h-11 w-11 shrink-0 rounded-full object-cover bg-slate-100"
                      />
                    ) : (
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#3a00e0] to-[#fab03a] text-sm font-semibold text-white"
                        aria-hidden="true"
                      >
                        {(testimonial.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-slate-900">{testimonial.name}</p>
                      <p className="text-xs text-slate-500">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="mb-3 flex gap-0.5" aria-label={`Note : ${testimonial.rating} sur 5`}>
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star
                        key={`testimonial-${testimonial.id}-star-${j}`}
                        className="h-3.5 w-3.5 fill-[#fab03a] text-[#fab03a]"
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed text-slate-600">{testimonial.content}</p>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PartnersCarousel />

      {/* Final CTA — mascotte pop-out bas-droit */}
      <section className="section-y px-4 overflow-visible">
        <div className="mx-auto max-w-4xl overflow-visible pb-8 sm:pb-12">
          <Reveal animation="fade-up" className="overflow-visible">
            <Card className={`relative overflow-visible border-none bg-gradient-to-br from-[#3a00e0] via-[#5b21ff] to-[#fab03a] shadow-2xl shadow-[0_20px_50px_-20px_rgba(58,0,224,0.35)] ${RADIUS_SURFACE}`}>
              <CardContent className="relative overflow-visible p-8 text-center sm:p-12">
                <div
                  className={`absolute inset-0 ${RADIUS_SURFACE} bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.14),transparent_50%)]`}
                  aria-hidden="true"
                />
                <RobotMascot
                  variant="popout"
                  size="hero"
                  className="pointer-events-none h-28 w-28 translate-x-[10%] translate-y-[18%] sm:h-52 sm:w-52 sm:translate-x-[14%] sm:translate-y-[22%] md:h-60 md:w-60"
                />
                <div className="relative pr-16 sm:pr-28 md:pr-36">
                  <EditableText
                    editKey="home.cta.title"
                    defaultValue="Prêt à commencer l'aventure ?"
                    as="h2"
                    className="mb-3 text-[1.75rem] font-bold tracking-tight text-white sm:text-3xl lg:text-[2.35rem]"
                  />
                  <EditableText
                    editKey="home.cta.subtitle"
                    defaultValue="Réservez un atelier découverte offert et laissez votre enfant explorer la robotique dans un cadre bienveillant et stimulant."
                    as="p"
                    multiline
                    className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg"
                  />
                  <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                    <Link to="/reserver">
                      <Button size="lg" className={`w-full ${RADIUS_PILL} bg-white px-8 py-6 text-lg text-[#3a00e0] shadow-lg hover:bg-[rgba(58,0,224,0.06)] sm:w-auto`}>
                        <EditableText editKey="home.cta.primary" defaultValue="Réserver maintenant" className="inline" />
                        <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                      </Button>
                    </Link>
                    <Link to="/contact">
                      <Button size="lg" variant="outline" className={`w-full ${RADIUS_PILL} border-white/80 px-8 py-6 text-lg text-white hover:bg-white/10 sm:w-auto`}>
                        <EditableText editKey="home.cta.secondary" defaultValue="Nous contacter" className="inline" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
